import { create } from 'zustand';
import { disablePush } from '@/lib/push';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { AuthUser, FeatureKey, Role } from '@/api/auth';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { clearUserLogout } from '@/lib/loginReturn';
import { sessionStorageAdapter } from '@/lib/secureStorage';
import { keepSections, userSections } from '@/lib/studentFields';

/** Roles that always have full access regardless of tariff features. */
const STAFF_ROLES: Role[] = ['admin', 'manager', 'curator'];

/**
 * LOCKOUT-SAFE feature check.
 *  - Non-student roles (admin/manager/curator) → always true.
 *  - `features` contains the `*` sentinel → true (all-access).
 *  - `features` includes the key → true.
 *  - `features` undefined/null (not yet loaded or backend didn't send) → TRUE on the site.
 *    We never hide a feature because of missing data.
 *  - Otherwise → false (student's tariff excludes this feature).
 *
 * ⚠️ В ПРИЛОЖЕНИИ — СТРОГО (`strict`, по умолчанию `IS_APP_BUILD`; D75, 26.09.2026): нет набора — раздела нет.
 * Меню приложения строится только из того, что назвал сервер (`lib/storeTabs.ts`), и всё приложение —
 * маршруты `RequireFeature`, плитки главной, экраны — судит так же. Сайт не меняется: там отсутствие набора
 * по-прежнему «показать всё». Одна правка на всё приложение, а не по экрану: разбросанные проверки
 * разошлись бы на первом же новом экране.
 */
export function computeHasFeature(user: AuthUser | null, key: FeatureKey, strict: boolean = IS_APP_BUILD): boolean {
  if (!user) return true; // not loaded yet — don't hide
  if (STAFF_ROLES.includes(user.role)) return true;
  const features = userSections(user); // `sections`, раньше `features` (student-api.md §4)
  if (features == null) return !strict; // сайт — безопасно к блокировке, приложение — только названное сервером
  if (features.includes('*')) return true;
  return features.includes(key);
}

type AuthState = {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;

  // actions
  setAuth: (user: AuthUser, access: string, refresh: string) => void;
  setTokens: (access: string, refresh: string) => void;
  setUser: (user: AuthUser) => void;
  logout: () => void;

  // selectors
  isAuthenticated: () => boolean;
  hasRole: (...roles: Role[]) => boolean;
  hasFeature: (key: FeatureKey) => boolean;
};

/**
 * Хранилище сессии.
 *
 * В браузере — `localStorage`, в приложении из магазина — нативное хранилище
 * платформы (см. `lib/secureStorage`): веб-страница его не читает, поэтому
 * успешный XSS больше не означает кражу токенов.
 *
 * ⚠️ Нативное хранилище АСИНХРОННОЕ. В первый миг после запуска состояние ещё
 * не восстановлено, поэтому судить о входе по `user` можно только после
 * `hasHydrated` — иначе защита маршрутов выбросит вошедшего на экран входа.
 * Срок жизни сессии всё равно решает сервер; здесь мы только храним.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,

      setAuth: (user, access, refresh) => {
        // Вошли — метка «вышел кнопкой» больше не нужна: следующее «выбросило» снова вернёт на место (loginReturn.ts).
        clearUserLogout();
        set({ user, accessToken: access, refreshToken: refresh });
        // ⚠️ ПУШИ ПОДКЛЮЧАЮТСЯ НЕ ЗДЕСЬ, а в оболочке ученика (`hooks/usePushSync.ts`, 01.10.2026, сборка
        // 1.1.1). С 25.09 по 01.10 вызова не было вовсе (D73, техдолг 290: APNs на сервере не был настроен).
        // Здесь он не вернулся намеренно: в ответе входа набора разделов может ещё не быть, а пуши
        // положены только ученику с куратором (`lib/pushAccess.ts`) — оболочка знает набор после `/auth/me`.
      },
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      // Ответ без набора разделов (смена языка, аватара) не стирает прежний набор того же человека.
      setUser: (user) => set((s) => ({ user: keepSections(s.user, user) })),
      logout: () => {
        // Метку устройства убираем ПЕРЕД сбросом сессии: запрос уходит с
        // токеном, которого через мгновение уже не будет.
        //
        // ⚠️ Без этого на общем телефоне следующий вошедший получал бы
        // уведомления предыдущего — с названиями его уроков на экране блокировки.
        //
        // ⚠️ КЛЮЧ ПЕРЕДАЁМ ЯВНО (01.10.2026). Перехватчик запросов асинхронный и читает ключ из хранилища
        // уже ПОСЛЕ строки ниже — без параметра DELETE уходил без `Authorization`. Это запасной путь для
        // вынужденного выхода; кнопки «Выйти» снимают метку сами, с ожиданием (`await disablePush()`), и
        // сюда приходят уже без метки.
        void disablePush(get().accessToken);
        set({ user: null, accessToken: null, refreshToken: null });
      },

      isAuthenticated: () => Boolean(get().accessToken),
      hasRole: (...roles) => {
        const role = get().user?.role;
        return role ? roles.includes(role) : false;
      },
      hasFeature: (key) => computeHasFeature(get().user, key),
    }),
    {
      name: 'onebilim-auth',
      storage: createJSONStorage(() => sessionStorageAdapter),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),

      /*
       * ⚠️ `onRehydrateStorage` С ПОДКЛЮЧЕНИЕМ ПУШЕЙ ЗДЕСЬ НЕТ — оно в оболочке ученика
       * (`hooks/usePushSync.ts`): оболочка появляется при каждом запуске с живой сессией, так что метка
       * устройства, не доехавшая с первого раза, доезжает при следующем запуске (сессия живёт 60 дней,
       * путь входа человек больше не проходит).
       */
    },
  ),
);
